import type { UnifiedChatRichContext } from '../../../data-access/types';
import type { MealFoodAmount, Signal } from '@demind-inc/core';

export type AnyRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is AnyRecord =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const nonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const nonEmptyArray = (value: unknown): value is unknown[] =>
  Array.isArray(value) && value.length > 0;

const hasMealShape = (value: AnyRecord): boolean => {
  return (
    isRecord(value.meal_content) ||
    nonEmptyArray(value.meal_details) ||
    nonEmptyArray(value.food_items) ||
    nonEmptyArray(value.foodItems) ||
    nonEmptyString(value.title)
  );
};

export const isMealSignal = (signal: Signal | null | undefined): boolean => {
  const source =
    typeof signal?.signal_source === 'string' ? signal.signal_source.trim().toLowerCase() : '';
  return source === 'meal';
};

const hasNonEmptyMealDetails = (data: AnyRecord): boolean => {
  const topLevel = data.meal_details;
  if (Array.isArray(topLevel) && topLevel.length > 0) return true;
  if (isRecord(data.meal_content)) {
    const nested = (data.meal_content as AnyRecord).meal_details;
    if (Array.isArray(nested) && nested.length > 0) return true;
  }
  return false;
};

const hasExplicitNullFoodItems = (data: AnyRecord): boolean => {
  return data.food_items === null || data.foodItems === null;
};

export const getMealSignalDataList = (
  richContext: UnifiedChatRichContext | null | undefined
): AnyRecord[] => {
  if (!richContext || !Array.isArray(richContext.signals)) return [];

  return richContext.signals.flatMap((signal) => {
    if (!isMealSignal(signal) || !isRecord(signal.data)) return [];
    const data = signal.data as AnyRecord;
    if (hasExplicitNullFoodItems(data) && !hasNonEmptyMealDetails(data)) return [];
    return [data];
  });
};

export const getMealDetailsFromRichContextSignals = (
  richContext: UnifiedChatRichContext | null | undefined
): AnyRecord[] => {
  const mealDetails: AnyRecord[] = [];
  const dedupe = new Set<string>();

  const pushDetail = (detail: AnyRecord) => {
    const key = JSON.stringify({
      start_time: detail.start_time ?? detail.startTime ?? null,
      end_time: detail.end_time ?? detail.endTime ?? null,
      type: detail.type ?? null,
      title: detail.title ?? null,
      food_items: detail.food_items ?? detail.foodItems ?? null,
    });
    if (dedupe.has(key)) return;
    dedupe.add(key);
    mealDetails.push(detail);
  };

  const pushDetailList = (rows: unknown[]) => {
    for (const row of rows) {
      if (isRecord(row)) pushDetail(row);
    }
  };

  for (const data of getMealSignalDataList(richContext)) {
    if (isRecord(data.meal_content)) {
      const nestedDetails = (data.meal_content as AnyRecord).meal_details;
      if (Array.isArray(nestedDetails)) pushDetailList(nestedDetails);
    }
    const topLevelDetails = data.meal_details;
    if (Array.isArray(topLevelDetails)) pushDetailList(topLevelDetails);
  }

  return mealDetails;
};

export const getMealSummaryPayloadFromSignals = (
  richContext: UnifiedChatRichContext | null | undefined
): AnyRecord | null => {
  let fallbackMealPayload: AnyRecord | null = null;
  const collectedMealDetails = getMealDetailsFromRichContextSignals(richContext);

  for (const data of getMealSignalDataList(richContext)) {
    if (isRecord(data.meal_content)) {
      const basePayload = data.meal_content as AnyRecord;
      const existingMealDetails = Array.isArray(basePayload.meal_details)
        ? (basePayload.meal_details as unknown[]).filter(isRecord)
        : [];
      if (existingMealDetails.length > 0 || collectedMealDetails.length === 0) return basePayload;
      return { ...basePayload, meal_details: collectedMealDetails };
    }
    if (!fallbackMealPayload && hasMealShape(data)) fallbackMealPayload = data;
  }

  if (fallbackMealPayload) {
    const existingMealDetails = Array.isArray(fallbackMealPayload.meal_details)
      ? (fallbackMealPayload.meal_details as unknown[]).filter(isRecord)
      : [];
    if (existingMealDetails.length > 0 || collectedMealDetails.length === 0)
      return fallbackMealPayload;
    return { ...fallbackMealPayload, meal_details: collectedMealDetails };
  }

  if (collectedMealDetails.length > 0) return { meal_details: collectedMealDetails };
  return null;
};

const normalizeDescription = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const t = value.trim();
  return t.length > 0 ? t : undefined;
};

const toFoodItems = (raw: unknown): MealFoodAmount[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (item): item is { name: string; amount?: unknown; unit?: unknown } =>
        typeof item === 'object' && item !== null && typeof (item as AnyRecord).name === 'string'
    )
    .map((item) => {
      const amount = typeof item.amount === 'number' && item.amount > 0 ? item.amount : 1;
      const unitRaw = item.unit;
      const unit = typeof unitRaw === 'string' && unitRaw.trim() ? unitRaw.trim() : undefined;
      const base = { name: item.name, amount };
      return (unit !== undefined ? { ...base, unit } : base) as MealFoodAmount;
    });
};

const extractFromMealPayload = (
  data: AnyRecord
): {
  foodItems: MealFoodAmount[];
  title: string;
  description?: string;
  macros?: Record<string, number>;
  micros?: Record<string, number>;
} | null => {
  const foodItems = toFoodItems(data.food_items ?? data.foodItems);
  const topTitle = typeof data.title === 'string' ? data.title.trim() : '';
  if (foodItems.length === 0 && !topTitle) return null;
  const macros =
    data.macros && typeof data.macros === 'object' && !Array.isArray(data.macros)
      ? (data.macros as Record<string, number>)
      : undefined;
  const micros =
    data.micros && typeof data.micros === 'object' && !Array.isArray(data.micros)
      ? (data.micros as Record<string, number>)
      : undefined;
  const description = normalizeDescription(data.description);
  return { foodItems, title: topTitle || foodItems.map((f) => f.name).join(', '), description, macros, micros };
};

export function extractMealEditorDefaultsFromRichContext(
  richContext: UnifiedChatRichContext | null | undefined,
  _eventStart: string,
  _eventEnd: string,
  _mealTypeHint?: string
): {
  foodItems: MealFoodAmount[];
  title: string;
  description?: string;
  macros?: Record<string, number>;
  micros?: Record<string, number>;
} {
  if (!richContext) {
    return { foodItems: [], title: '' };
  }

  const signalPayload = getMealSummaryPayloadFromSignals(richContext);
  if (signalPayload) {
    const result = extractFromMealPayload(signalPayload);
    if (result) return result;
  }

  // Fallback: read from content.payload for meal_suggestion content (e.g., all-plan v7 schedules)
  const rawContent = richContext.content;
  const contentList = rawContent ? (Array.isArray(rawContent) ? rawContent : [rawContent]) : [];
  for (const content of contentList) {
    if (!content || typeof content !== 'object' || Array.isArray(content)) continue;
    const contentType =
      typeof (content as AnyRecord).type === 'string'
        ? ((content as AnyRecord).type as string).trim().toLowerCase()
        : '';
    if (!contentType.includes('meal')) continue;
    const contentPayload = (content as AnyRecord).payload;
    if (!contentPayload || typeof contentPayload !== 'object' || Array.isArray(contentPayload))
      continue;
    const mealData = isRecord((contentPayload as AnyRecord).meal_content)
      ? ((contentPayload as AnyRecord).meal_content as AnyRecord)
      : (contentPayload as AnyRecord);
    const result = extractFromMealPayload(mealData);
    if (result) return result;
  }

  return { foodItems: [], title: '' };
}
